import { format } from "date-fns"

// songs: array of song objects with an optional `duration` field (seconds as float)
export function formatPlaylistDuration(songs) {
  if (!songs?.length) return null
  const totalSeconds = songs.reduce((sum, song) => sum + (song.duration ?? 0), 0)
  if (totalSeconds === 0) return null
  const totalMinutes = Math.round(totalSeconds / 60)
  if (totalMinutes < 60) return `${totalMinutes}min`
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return minutes > 0 ? `${hours}hr ${minutes}min` : `${hours}hr`
}

export const DateDifferenceInWords = (a, b) => {
  const _MS_PER_SEC = 1000
  const _MS_PER_MIN = _MS_PER_SEC * 60
  const _MS_PER_HOUR = _MS_PER_MIN * 60
  const _MS_PER_DAY = _MS_PER_HOUR * 24

  const utc1 = Date.UTC(
    a.getFullYear(),
    a.getMonth(),
    a.getDate(),
    a.getHours(),
    a.getMinutes(),
    a.getSeconds(),
  )
  const utc2 = Date.UTC(
    b.getFullYear(),
    b.getMonth(),
    b.getDate(),
    b.getHours(),
    b.getMinutes(),
    b.getSeconds(),
  )
  const diffMS = utc2 - utc1
  const absDiffMS = Math.abs(diffMS)
  const inFuture = utc1 > utc2
  const preText = inFuture ? "In " : ""
  const postText = inFuture ? "" : " ago"

  if (absDiffMS < _MS_PER_MIN) {
    // display seconds
    return {
      fullText: `<1 min${postText}`,
      inFuture: inFuture,
      units: "seconds",
      diffInSeconds: absDiffMS * _MS_PER_SEC,
      updateInMS: 20 * _MS_PER_SEC,
    }
  } else if (absDiffMS < _MS_PER_HOUR) {
    // display minutes
    return {
      fullText: `${preText}${Math.abs(
        Math.floor(diffMS / _MS_PER_MIN),
      )} min${postText}`,
      inFuture: inFuture,
      units: "minutes",
      diffInSeconds: absDiffMS * _MS_PER_SEC,
      updateInMS: 60 * _MS_PER_SEC,
    }
  } else if (absDiffMS < _MS_PER_DAY) {
    // display hours
    return {
      fullText: `${preText}${Math.abs(
        Math.floor(diffMS / _MS_PER_HOUR),
      )} hrs${postText}`,
      inFuture: inFuture,
      units: "hours",
      diffInSeconds: absDiffMS * _MS_PER_SEC,
      updateInMS: 20 * 60 * _MS_PER_SEC,
    }
  } else {
    // return date
    return {
      fullText: SmartDateFormat({ date: a, includeTime: true }),
      inFuture: inFuture,
      units: "days",
      diffInSeconds: absDiffMS * _MS_PER_SEC,
      updateInMS: 100 * 60 * _MS_PER_SEC,
    }
  }
}

export const isSameDay = (a, b) => {
  if (!a || !b) return false
  a = new Date(a)
  b = new Date(b)
  return (
    !!a &&
    !!b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

export const SmartDateFormat = ({
  date = null,
  dateStr = null,
  includeTime = true,
  includeOrdinal = true,
}) => {
  if (!date && !dateStr) return ""
  date = date ? date : new Date(dateStr)
  const today = new Date()

  const isThisYear = today.getFullYear() === date.getFullYear()
  const isThisWeek = format(today, "w") === format(date, "w")
  const isToday =
    isThisYear &&
    today.getMonth() === date.getMonth() &&
    today.getDate() === date.getDate()
  const timeFormat = includeTime ? "h:mma" : ""

  if (date) {
    const ordinalStr = includeOrdinal ? nth(format(date, "d")) : ""

    return isToday
      ? includeTime
        ? "Today " + format(date, timeFormat)
        : "Today"
      : isThisWeek
        ? format(date, `EEEE LLLL d`) +
          ordinalStr +
          (includeTime ? format(date, ` ${timeFormat}`) : "")
        : isThisYear
          ? format(date, `EEEE LLLL d`) +
            ordinalStr +
            (includeTime ? format(date, ` ${timeFormat}`) : "")
          : format(date, `yyyy LLLL d`) +
            ordinalStr +
            (includeTime ? format(date, ` ${timeFormat}`) : "")
  } else {
    console.log("Failed to parse date, and format:", date)
    return ""
  }
}

export const timeOnly = ({ date = null, dateStr = null }) => {
  if (!date && !dateStr) return ""
  date = date ? date : new Date(dateStr)
  return format(date, "h:mma")
}

export const timeToMinutesSeconds = (time = 0) => {
  // @ts-ignore
  const totalTimeInSeconds = parseInt(time, 10)
  const minutes = Math.floor(totalTimeInSeconds / 60)
  const seconds = totalTimeInSeconds - minutes * 60

  if (seconds < 10) return `${minutes}:0${seconds}`
  return `${minutes}:${seconds}`
}

export const DayFormat = (dateStr) => {
  if (!dateStr) return ""
  const date = new Date(dateStr)
  const ordinalStr = nth(format(date, "d"))
  return format(date, `EEEE LLLL d`) + ordinalStr
}

export const DateFormat = (dateStr) => {
  if (!dateStr) return ""
  const date = new Date(dateStr)
  return format(date, `MM/dd/yyyy`)
}

// e.g. "July 15th 2026"
export const MonthDayYearFormat = (dateStr) => {
  if (!dateStr) return ""
  const date = new Date(dateStr)
  const ordinalStr = nth(format(date, "d"))
  return `${format(date, "LLLL d")}${ordinalStr} ${format(date, "yyyy")}`
}

// e.g. "July 24th - Friday at 8:00pm"
export const ShowDateFormat = (dateStr) => {
  if (!dateStr) return ""
  const date = new Date(dateStr)
  const ordinalStr = nth(format(date, "d"))
  const monthDay = `${format(date, "LLLL d")}${ordinalStr}`
  const weekday = format(date, "EEEE")
  const time = format(date, "h:mma").toLowerCase()
  return `${monthDay} - ${weekday} at ${time}`
}

// Formats event date+time in the event's timezone, e.g. "Friday, April 4 at 7:00 PM"
// Includes year when the event is in a different year than today.
export const EventDateFormat = (dateStr, timezone) => {
  if (!dateStr) return ""
  const date = new Date(dateStr)
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  const isThisYear = new Date().getFullYear() === new Date(date.toLocaleString("en-US", { timeZone: tz })).getFullYear()
  return new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    weekday: "long",
    month: "long",
    day: "numeric",
    ...(isThisYear ? {} : { year: "numeric" }),
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date)
}

// Formats event end time with timezone abbreviation in the event's timezone, e.g. "11:00 PM EDT"
export const EventTimeFormat = (dateStr, timezone) => {
  if (!dateStr) return ""
  const date = new Date(dateStr)
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  return new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  }).format(date)
}

export const nth = function (d) {
  if (d > 3 && d < 21) return "th"
  switch (d % 10) {
    case 1:
      return "st"
    case 2:
      return "nd"
    case 3:
      return "rd"
    default:
      return "th"
  }
}
